
import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';
import { ArrowLeft, PieChart as PieChartIcon, TrendingDown } from "lucide-react";
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { categoryDataService } from '@/services/CategoryDataService';
import type { PieChartCategoryData, CategoryFilters } from '@/types/CategoryTypes';
import { Progress } from '@/components/ui/progress';

const COLORS = [
  '#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF',
  '#FF9F40', '#FF6384', '#C9CBCF', '#4BC0C0', '#FF6384'
];

interface ExpensePieChartProps {
  currentPlan: any;
  selectedMonth: number;
  selectedYear: number;
  accountId: string;
}

export default function ExpensePieChart({ currentPlan, selectedMonth, selectedYear, accountId }: ExpensePieChartProps) {
  const [chartData, setChartData] = useState<PieChartCategoryData[]>([]);
  const [drillDownData, setDrillDownData] = useState<any[] | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<PieChartCategoryData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  const loadPieChartData = useCallback(async () => {
    if (!accountId || !currentPlan?.id) {
      setIsLoading(false);
      setChartData([]);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const filters: CategoryFilters = {
        account_id: accountId,
        monthly_plan_id: currentPlan.id,
        month: selectedMonth,
        year: selectedYear,
        category_type: 'expense'
      };

      const response = await categoryDataService.getPieChartData(filters);

      if (response.success && response.data) {
        setChartData(response.data);
      } else {
        setError(response.error || 'Erro ao carregar dados do gráfico');
        setChartData([]);
      }
    } catch (error) {
      console.error('Erro ao carregar dados do gráfico:', error);
      setError('Erro inesperado ao carregar dados');
      setChartData([]);
    } finally {
      setIsLoading(false);
    }
  }, [accountId, currentPlan?.id, selectedMonth, selectedYear]);

  useEffect(() => {
    if (currentPlan) {
      loadPieChartData();
    }
  }, [currentPlan, loadPieChartData]);

  const handlePieClick = (_data: any, index: number) => {
    const clickedCategory = chartData[index];
    if (!clickedCategory) return;

    const hasDetails = clickedCategory.hasSubcategories || clickedCategory.records.length > 0;
    if (!hasDetails) return;

    setSelectedCategory(clickedCategory);
    setDrillDownData(
      clickedCategory.hasSubcategories
        ? clickedCategory.subcategories.filter((sub: any) => sub.totalSpent > 0)
        : clickedCategory.records
    );
  };

  const handleBackToMain = () => {
    setSelectedCategory(null);
    setDrillDownData(null);
  };

  const CustomTooltip = ({ active, payload }: { active: any; payload: any; label: any }) => {
    if (active && payload && payload.length) {
      const data = payload[0];
      return (
        <div className="bg-white p-3 border rounded-lg shadow-lg">
          <p className="font-semibold">{data.payload.name}</p>
          <p className="text-sm">
            <span className="font-medium">Total: {formatCurrency(data.value)}</span>
          </p>
          {(data.payload.hasSubcategories || (data.payload.records?.length || 0) > 0) && (
            <p className="text-xs text-gray-500">Clique para ver detalhes</p>
          )}
        </div>
      );
    }
    return null;
  };

  // Legenda customizada simplificada com bolinhas coloridas
  const CustomLegend = ({ payload }: { payload: any }) => {
    return (
      <div className="flex flex-wrap justify-center gap-4 mt-4">
        {payload.map((entry: any, index: number) => (
          <div key={index} className="flex items-center gap-2">
            <div 
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: entry.color }}
            />
            <span className="text-sm text-gray-700">{entry.value}</span>
          </div>
        ))}
      </div>
    );
  };

  const getProgressColor = (pct: number): 'green' | 'yellow' | 'orange' | 'red' | 'default' => {
    if (pct < 50) return 'green';
    if (pct >= 50 && pct < 80) return 'yellow';
    if (pct >= 80 && pct <= 100) return 'orange';
    return 'red';
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <PieChartIcon className="w-5 h-5" />
            Análise de Despesas
          </CardTitle>
        </CardHeader>
        <CardContent className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600 mx-auto mb-2"></div>
            <p className="text-sm text-gray-600">Carregando dados...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <PieChartIcon className="w-5 h-5" />
            Análise de Despesas
          </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col items-center justify-center h-64">
          <TrendingDown className="w-16 h-16 text-red-400 mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Erro ao carregar dados
          </h3>
          <p className="text-gray-600 text-center mb-4">
            {error}
          </p>
          <Button onClick={loadPieChartData} variant="outline" size="sm">
            Tentar novamente
          </Button>
        </CardContent>
      </Card>
    );
  }

  if (!currentPlan || chartData.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <PieChartIcon className="w-5 h-5" />
            Análise de Despesas
          </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col items-center justify-center h-64">
          <TrendingDown className="w-16 h-16 text-gray-400 mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Nenhum gasto registrado
          </h3>
          <p className="text-gray-600 text-center">
            Registre algumas despesas para ver o gráfico de análise
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <PieChartIcon className="w-5 h-5" />
            Análise de Despesas
          </div>
          {drillDownData && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleBackToMain}
              className="flex items-center gap-2"
              aria-label="Voltar para visualização principal"
            >
              <ArrowLeft className="w-4 h-4" />
              Voltar
            </Button>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {!drillDownData ? (
          /* Visualização Principal - Gráfico de Pizza */
          <div className="space-y-4">
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={chartData}
                    cx="50%"
                    cy="50%"
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                    onClick={handlePieClick}
                    className="cursor-pointer"
                  >
                    {chartData.map((_, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip active={undefined} payload={undefined} label={undefined} />} />
            <Legend content={<CustomLegend payload={undefined} />} />
                </PieChart>
              </ResponsiveContainer>
            </div>
            
            {/* Lista de Categorias */}
            <div className="space-y-2">
              {chartData.map((category, index) => (
                (() => {
                  const hasDetails = category.hasSubcategories || category.records.length > 0;

                  return (
                <div 
                  key={category.id}
                  className={`flex items-center justify-between p-3 rounded-lg border transition-all ${
                    hasDetails ? 'cursor-pointer hover:bg-gray-50 hover:shadow-md' : 'bg-gray-50'
                  }`}
                  onClick={() => hasDetails && handlePieClick(null, index)}
                  role={hasDetails ? "button" : undefined}
                  tabIndex={hasDetails ? 0 : undefined}
                  aria-label={hasDetails ? `Ver detalhes da categoria ${category.name}` : undefined}
                >
                  <div className="flex items-center gap-3">
                    <div 
                      className="w-4 h-4 rounded-full"
                      style={{ backgroundColor: COLORS[index % COLORS.length] }}
                    />
                    <div>
                      <span className="font-medium">{category.name}</span>
                      {category.hasSubcategories && (
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="secondary" className="text-xs">
                            {category.subcategories.length} sub
                          </Badge>
                        </div>
                      )}
                      {!category.hasSubcategories && category.records.length > 0 && (
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="secondary" className="text-xs">
                            {category.records.length} registros
                          </Badge>
                        </div>
                      )}
                      {typeof category.planned === 'number' && category.planned > 0 && (
                        <div className="mt-2 w-full">
                          <Progress 
                            value={Math.min(Math.round(((category.value || 0) / (category.planned || 1)) * 100), 150)}
                            color={getProgressColor(((category.value || 0) / (category.planned || 1)) * 100)}
                          />
                          <div className="text-xs text-gray-600 mt-1">
                            {Math.round((((category.value || 0) / (category.planned || 1)) * 100))}% do planejado {formatCurrency(category.planned || 0)}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-red-600">{formatCurrency(category.value)}</p>
                    <p className="text-xs text-gray-500">
                      {((category.value / chartData.reduce((sum, cat) => sum + cat.value, 0)) * 100).toFixed(1)}% no mês
                    </p>
                  </div>
                </div>
                  );
                })()
              ))}
            </div>
          </div>
        ) : (
          /* Visualização de Drill-down - Lista de Subcategorias */
          <div className="space-y-4">
            <div className="text-center py-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Detalhes de {selectedCategory.name}
              </h3>
              <p className="text-sm text-gray-600">
                Total gasto: {formatCurrency(selectedCategory.value)}
                <span className="text-xs text-gray-500 ml-2">→&nbsp;
                  {(
                    selectedCategory && chartData.length > 0
                      ? ((selectedCategory.value / chartData.reduce((sum, c) => sum + c.value, 0)) * 100).toFixed(1)
                      : '0.0'
                  )}% do total mensal
                </span>
              </p>
              {typeof selectedCategory.planned === 'number' && selectedCategory.planned > 0 && (
                <div className="mt-2 w-full">
                  <Progress
                    value={Math.min(Math.round(((selectedCategory.value || 0) / (selectedCategory.planned || 1)) * 100), 150)}
                    color={getProgressColor(((selectedCategory.value || 0) / (selectedCategory.planned || 1)) * 100)}
                  />
                  <div className="text-xs text-gray-600 mt-1">
                    {Math.round((((selectedCategory.value || 0) / (selectedCategory.planned || 1)) * 100))}% do planejado {formatCurrency(selectedCategory.planned || 0)}
                  </div>
                </div>
              )}
            </div>

            {selectedCategory.hasSubcategories ? (
              <div className="space-y-3">
                {drillDownData.map((subcategory) => (
                  <Card key={subcategory.id} className="border-l-4 border-l-red-400">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-medium text-gray-900">{subcategory.name}</h4>
                          <Badge variant="destructive" className="mt-1 text-xs">
                            Sub de Despesa
                          </Badge>
                          <div className="text-xs text-gray-500 mt-1">
                            {(subcategory.records?.length || 0)} registros
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold text-red-600">
                            {formatCurrency(subcategory.totalSpent)}
                          </p>
                          <p className="text-xs text-gray-500">
                            {selectedCategory.value > 0 ? 
                              ((subcategory.totalSpent / selectedCategory.value) * 100).toFixed(1) : 0}% do total
                          </p>
                        </div>
                      </div>
                      <div className="mt-3 space-y-2">
                        {(subcategory.records || []).map((rec: any) => (
                          <div key={rec.id} className="flex items-center justify-between">
                            <div className="text-sm text-gray-900">
                              {rec.description || 'Registro'}
                            </div>
                            <div className="text-sm text-gray-700">
                              {format(new Date(rec.date), 'dd MMM', { locale: ptBR })}
                              {' '}
                              {formatCurrency(rec.amount)}
                            </div>
                          </div>
                        ))}
                      </div>
                      {typeof subcategory.planned === 'number' && subcategory.planned > 0 && (
                        <div className="mt-3 w-full">
                          <Progress 
                            value={Math.min(Math.round(((subcategory.totalSpent || 0) / (subcategory.planned || 1)) * 100), 150)}
                            color={getProgressColor(((subcategory.totalSpent || 0) / (subcategory.planned || 1)) * 100)}
                          />
                          <div className="text-xs text-gray-600 mt-1">
                            {Math.round((((subcategory.totalSpent || 0) / (subcategory.planned || 1)) * 100))}% do planejado {formatCurrency(subcategory.planned || 0)}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="space-y-3">
                <Card className="border-l-4 border-l-red-400">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-medium text-gray-900">Registros</h4>
                        <Badge variant="destructive" className="mt-1 text-xs">
                          Categoria de Despesa
                        </Badge>
                        <div className="text-xs text-gray-500 mt-1">
                          {(drillDownData?.length || 0)} registros
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold text-red-600">
                          {formatCurrency(selectedCategory.value)}
                        </p>
                      </div>
                    </div>
                    <div className="mt-3 space-y-2">
                      {(drillDownData || []).map((rec: any) => (
                        <div key={rec.id} className="flex items-center justify-between">
                          <div className="text-sm text-gray-900">
                            {rec.description || 'Registro'}
                          </div>
                          <div className="text-sm text-gray-700">
                            {format(new Date(rec.date), 'dd MMM', { locale: ptBR })}
                            {' '}
                            {formatCurrency(rec.amount)}
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
